import Axios, { AxiosError, AxiosPromise, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { TOptions } from 'i18next';
import { isEqual } from 'lodash-es';
import { when } from 'mobx';

import { USE_MOCKS } from '@shared/constants';
import { IncludeOrUndefined } from '@shared/interfaces/IncludeOrUndefined';
import { NotificationsStore } from '@shared/stores/NotificationsStore/NotificationsStore';
import { Notification } from '@shared/stores/NotificationsStore/models/Notification';
import { NotificationType } from '@shared/stores/NotificationsStore/models/NotificationType';

import { ApiName } from './ApiName';
import { areMocksEnabled } from './enableMocks';
import { ApiResult } from './models/ApiResult';
import { GqlQuery, GraphQlResponse } from './models/GraphQL';

export interface CallApiError<TError> {
	message: string;
	data?: TError;
	status?: number;
	statusText?: string;
	axiosError?: AxiosError<TError>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createApiError = <T>(e: any): CallApiError<T> => {
	return {
		message: e.message || '',
		data: e.response?.data,
		status: e.response?.status,
		statusText: e.response?.statusText,
		axiosError: Axios.isAxiosError(e) ? e : undefined,
	};
};

interface ApiNotifications<T, TError> {
	notifications: NotificationsStore;
	notificationPrefix: string;
	notificationOptions?: TOptions | ((data: T, error: TError) => TOptions);
	showSuccessNotification?: boolean;
	showErrorNotification?: boolean;
	adjustErrorNotification?: (notification: Notification, result: ApiResult<T, TError>, e: Error) => Notification | Promise<Notification>;
}

export type CallApiRequest<T, TError = unknown> = {
	result: ApiResult<T, TError>;
	promise: AxiosPromise<T>;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	onSuccess?: (data: T) => Promise<unknown> | void;
	onError?: (e: CallApiError<TError>) => void;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	onFinally?: () => Promise<unknown> | void;
	transformData?: (data: T) => T;
} & IncludeOrUndefined<ApiNotifications<T, TError>>;

// Common pattern used in stores to call an API.
export const callApi = async <T, TError>(request: CallApiRequest<T, TError>) => {
	const {
		result,
		promise,
		notifications,
		notificationPrefix,
		notificationOptions,
		showSuccessNotification = false,
		showErrorNotification = true,
		adjustErrorNotification,
		transformData = null,
		onSuccess = null,
		onError = null,
		onFinally = null,
	} = request;

	result.startLoading();

	try {
		const res = await promise;

		if (transformData) {
			res.data = transformData(res.data);
		}

		await onSuccess?.(res.data);

		result.setSuccess(res.data, res.status, res.statusText);

		if (showSuccessNotification && notifications && notificationPrefix) {
			const options = typeof notificationOptions === 'function' ? notificationOptions(res.data, null) : notificationOptions;
			notifications.addApiNotification(true, notificationPrefix, options);
		}

		return res.data;
	} catch (e) {
		if (Axios.isCancel(e)) {
			result.setCancelled();

			return undefined;
		}

		onError?.(createApiError(e));

		if (!Axios.isAxiosError(e)) {
			// eslint-disable-next-line no-console
			console.error(e);
		}

		const message = e?.message || '';
		const { status, data, statusText, request } = e.response || {};
		result.setError(message, status, data, statusText, request);

		if (showErrorNotification && notifications && notificationPrefix) {
			const options = typeof notificationOptions === 'function' ? notificationOptions(null, data) : notificationOptions;
			let notification = await notifications.buildApiNotification(NotificationType.Error, notificationPrefix, options);
			if (adjustErrorNotification) {
				notification = await adjustErrorNotification(notification, result, e);
			}
			if (notification) {
				notifications.addNotification(notification);
			}
		}
	} finally {
		await onFinally?.();
	}
};

export const ignoreForbiddenErrors = <T>(notification: Notification, result: ApiResult<T>) => (result.isForbidden ? null : notification);

export const Ok = <T>(data: T = null): AxiosResponse<T> => {
	return {
		data,
		status: 200,
		statusText: 'OK',
		config: null,
		headers: null,
	};
};

export const NotFound = (data: unknown = null): AxiosError => {
	return {
		config: null,
		code: '404',
		response: { data, status: 404, statusText: 'Not Found', headers: null, config: null },
		name: 'fake 404 name',
		message: 'fake 404 message',
		isAxiosError: true,
		toJSON: null,
	};
};

export const BadRequest = (data: unknown = null, config: InternalAxiosRequestConfig = null): AxiosError => {
	return {
		config,
		code: '400',
		response: { data, status: 400, statusText: 'Bad Request', headers: null, config: null },
		name: 'fake 400 name',
		message: 'fake 400 message',
		isAxiosError: true,
		toJSON: null,
	};
};

export const Conflict = (data: unknown = null): AxiosError => {
	return {
		config: null,
		code: '409',
		response: { data, status: 409, statusText: 'Conflict', headers: null, config: null },
		name: 'fake 409 name',
		message: 'fake 409 message',
		isAxiosError: true,
		toJSON: null,
	};
};

export const InternalServerError = (): AxiosError => {
	return {
		config: null,
		code: '500',
		response: { data: null, status: 500, statusText: 'Internal Server Error', headers: null, config: null },
		name: 'fake 500 name',
		message: 'fake 500 message',
		isAxiosError: true,
		toJSON: null,
	};
};

export const Forbidden = (): AxiosError => {
	return {
		config: null,
		code: '403',
		response: { data: null, status: 403, statusText: 'Forbidden', headers: null, config: null },
		name: 'fake 403 name',
		message: 'fake 403 message',
		isAxiosError: true,
		toJSON: null,
	};
};

export const apiIsLoading = (...apis: Array<{ isLoading: boolean }>) => {
	return apis.some((a) => a.isLoading);
};

export const apiIsFailure = (...apis: Array<{ isFailure: boolean }>) => {
	return apis.some((a) => a.isFailure);
};

export const apiIsDone = (...apis: Array<{ isDone: boolean }>) => {
	return apis.every((a) => a.isDone);
};

export const Gql = <T>(data: T = null, errors: Array<Record<string, unknown>> = undefined): GraphQlResponse<T> => {
	return {
		data,
		errors,
	};
};

export const spinDuringOperationThenRefresh = async (
	refreshWatcher: ApiResult<void>,
	operation: () => Promise<void>,
	refresh: () => Promise<void>,
) => {
	refreshWatcher.startLoading();
	try {
		await operation();
		await refresh();
	} finally {
		refreshWatcher.setSuccess();
	}
};

export const matchesGqlQuery = (c: AxiosRequestConfig, query: GqlQuery) => isEqual(JSON.parse(c.data), query);

export const checkGqlResponseHasError = <T>(res: AxiosResponse<GraphQlResponse<T>>, apiName: ApiName) => {
	if (!res.data || res.data.errors?.length > 0) {
		throw Error(`Error calling ${apiName} API: ${res.data?.errors.join('\n')}`);
	}
};

export const gqlTransformer =
	<T>(apiName: ApiName) =>
	(res: AxiosResponse<GraphQlResponse<T>>): AxiosResponse<T> => {
		checkGqlResponseHasError(res, apiName);

		return {
			...res,
			data: res.data?.data,
		};
	};
/**
 * Runs callFunc if result is not started or failed. Guarantees the ApiResult is completed (either success or fail) before resolving
 */
export const callIfNeeded = async <T>(result: ApiResult<T>, callFunc: () => Promise<T>) => {
	if (result.isNotStarted || result.isFailure) {
		await callFunc();
	}

	await when(() => result.isDone);

	return result.data;
};

export const enableMocksFromUrl = (apiName: ApiName) => {
	const urlParams = new URLSearchParams(window.location.search.toLocaleLowerCase());
	const mockNames = urlParams.get(USE_MOCKS);
	const mockNameArray = mockNames?.split(',') || [];
	mockNameArray.forEach((mockName) => {
		if (mockName === apiName.toLocaleLowerCase()) {
			areMocksEnabled[apiName] = true;
		}
	});
};

export const assignApiResult = <T>(target: ApiResult<T>, source: ApiResult<T>) => {
	if (source.isSuccess) {
		target.setSuccess(source.data, source.statusCode, source.statusText);
	} else if (source.isCancelled) {
		target.setCancelled();
	} else {
		target.setError(source.error, source.statusCode, source.errorResponse, source.statusText);
	}
};
